import { useState } from 'react';
import I18n from 'I18n';
import { getInvoiceProperty } from 'invoices-ui-lib/utils/property';
import UIButton from 'UIComponents/button/UIButton';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UIIcon from 'UIComponents/icon/UIIcon';
import FormattedCurrency from 'I18n/components/FormattedCurrency';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedNumber from 'I18n/components/FormattedNumber';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UISearchInput from 'UIComponents/input/UISearchInput';
import { CANDY_APPLE } from 'HubStyleTokens/colors';
import UIToggleGroup from 'UIComponents/input/UIToggleGroup';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UIPaginator from 'UIComponents/paginator/UIPaginator';
import PaymentOption from './PaymentOption';
import useApplyPayments from '../../hooks/useApplyPayments';
import RecordPaymentTabs from './RecordPaymentTabs';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const ApplyExistingPaymentTab = props => {
  const {
    invoice,
    onSuccess,
    onFailure,
    onReject,
    selectedTab,
    setSelectedTab
  } = props;
  const {
    currentPage,
    pageCount,
    total,
    balanceDue,
    balanceSelected,
    loadingQuery,
    loadingMutation,
    options,
    selections,
    selectionsToDisplay,
    handleOnSearch,
    handleOnSelect,
    handleOnPageChange,
    handleOnConfirm,
    isSelected
  } = useApplyPayments(invoice, onSuccess, onFailure);
  const [isLoading, setIsLoading] = useState(false);
  const currency = getInvoiceProperty(invoice, 'hs_currency');
  const selectedExceedsBalance = balanceSelected > balanceDue;
  const shouldDisableConfirmButton = loadingQuery || loadingMutation || selections.length === 0 || selectedExceedsBalance;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(UIDialogBody, {
      children: [/*#__PURE__*/_jsx(RecordPaymentTabs, {
        selectedTab: selectedTab,
        setSelectedTab: setSelectedTab,
        shouldDisableTabs: isLoading
      }), /*#__PURE__*/_jsx(UIFormControl, {
        className: "p-top-4",
        children: /*#__PURE__*/_jsx(UISearchInput, {
          "data-test-id": "record-existing-payment-search-input",
          onChange: event => handleOnSearch(event.target.value),
          placeholder: I18n.text('invoicesHighlightCard.modals.applyExistingPayment.searchPlaceholder')
        })
      }), selectionsToDisplay.length > 0 ? /*#__PURE__*/_jsx(UIFormControl, {
        label: /*#__PURE__*/_jsx("div", {
          "data-test-id": "selections-label",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "invoicesHighlightCard.modals.applyExistingPayment.subheader.selected"
          })
        }),
        children: /*#__PURE__*/_jsx(UIToggleGroup, {
          className: "m-top-4",
          name: "separately-selected-payment-records",
          children: selectionsToDisplay.map(option => /*#__PURE__*/_jsx(PaymentOption, {
            option: option,
            currencyCode: currency,
            balanceDue: balanceDue,
            selected: isSelected(option.id),
            onChange: handleOnSelect
          }, option.id))
        })
      }) : null, total === 0 ? /*#__PURE__*/_jsx("div", {
        className: "m-top-4",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoicesHighlightCard.modals.applyExistingPayment.noResults"
        })
      }) : null, total && options.length > 0 ? /*#__PURE__*/_jsx(UIFormControl, {
        label: /*#__PURE__*/_jsx("div", {
          "data-test-id": "options-label",
          children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "invoicesHighlightCard.modals.applyExistingPayment.subheader.payments_jsx",
            elements: {
              FormattedNumber
            },
            options: {
              value: total
            }
          })
        }),
        children: /*#__PURE__*/_jsx(UIToggleGroup, {
          className: "m-top-4",
          name: "selected-payment-records",
          children: options.map(option => /*#__PURE__*/_jsx(PaymentOption, {
            option: option,
            currencyCode: currency,
            balanceDue: balanceDue,
            selected: isSelected(option.id),
            onChange: handleOnSelect
          }, option.id))
        })
      }) : null, pageCount ? /*#__PURE__*/_jsx(UIPaginator, {
        className: "m-top-4",
        pageCount: pageCount,
        showFirstLastButtons: false,
        onPageChange: event => handleOnPageChange(event.target.value),
        page: currentPage
      }) : null, loadingQuery ? /*#__PURE__*/_jsx(UILoadingSpinner, {}) : null]
    }), /*#__PURE__*/_jsxs(UIDialogFooter, {
      children: [/*#__PURE__*/_jsx(UITooltip, {
        disabled: !shouldDisableConfirmButton,
        title: selectedExceedsBalance ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoicesHighlightCard.modals.applyExistingPayment.confirmButton.exceedsBalance"
        }) : null,
        children: /*#__PURE__*/_jsx(UILoadingButton, {
          use: "primary",
          onClick: () => {
            setIsLoading(true);
            handleOnConfirm();
          },
          disabled: shouldDisableConfirmButton,
          "data-test-id": "add-existing-payment-confirm-button",
          loading: isLoading,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "invoicesHighlightCard.modals.applyExistingPayment.confirmButton.text",
            options: {
              count: selections.length > 1 ? 2 : 1
            }
          })
        })
      }), /*#__PURE__*/_jsx(UIButton, {
        onClick: onReject,
        disabled: isLoading,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoicesHighlightCard.modals.applyExistingPayment.cancelButton"
        })
      }), /*#__PURE__*/_jsxs("div", {
        children: [selectedExceedsBalance ? /*#__PURE__*/_jsxs("span", {
          children: [/*#__PURE__*/_jsx(UIIcon, {
            name: "warning",
            color: CANDY_APPLE
          }), ' ']
        }) : null, /*#__PURE__*/_jsx("span", {
          "data-test-id": "balance-due-message",
          children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "invoicesHighlightCard.modals.applyExistingPayment.balanceSelected_jsx",
            elements: {
              FormattedCurrency
            },
            options: {
              currencyCode: currency,
              valueSelected: balanceSelected,
              valueTotal: balanceDue
            }
          })
        })]
      })]
    })]
  });
};
export default ApplyExistingPaymentTab;